import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Link,
} from '@mui/material';

import { Link as RouterLink } from 'react-router-dom';
import { AccountBalance as APIcon } from '@mui/icons-material';
import { db } from '../firebase/config';
import { collection, getDocs, orderBy, query, doc, getDoc, where, documentId } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';

const DIRECT_TYPES = ['company', 'partner', 'vendor'];

const partnerSubAccountLabels = {
  lead: 'Lead',
  sales: 'Sales',
  support: 'Sales Support',
  client_pm: 'Client Relations',
  team_pm: 'Team Project Manager',
  administration: 'Administration',
  company: 'Company',
};

const resolveSubAccountLabel = (accountType, subAccount, overrides = {}) => {
  if (subAccount === null || subAccount === undefined || subAccount === '') return '—';
  const normalized = typeof subAccount === 'string' ? subAccount : String(subAccount);
  const loweredType = typeof accountType === 'string' ? accountType.toLowerCase() : '';
  if (loweredType === 'partner' && partnerSubAccountLabels[normalized]) return partnerSubAccountLabels[normalized];
  if (overrides[normalized]) return overrides[normalized];
  return normalized;
};

export default function AccountsPayable() {
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [entities, setEntities] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [filters, setFilters] = useState({ entityType: '', start: '', end: '', includeInactive: false, includeZero: false });
  const [drill, setDrill] = useState({ open: false, entityId: '', entityName: '', type: '' });
  const [contractTitleById, setContractTitleById] = useState({});
  const [subAccountNameById, setSubAccountNameById] = useState({});
  const [bankTotal, setBankTotal] = useState(0);

const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n || 0));

// Round to 2 decimal places to avoid floating-point precision errors in currency calculations
const roundCurrency = (n) => Math.round((Number(n) || 0) * 100) / 100;

const resolveDescription = (entry) => {
  if (!entry) return '';
  const candidates = [entry.memo, entry.description, entry.note, entry.reference];
  const match = candidates.find((value) => typeof value === 'string' && value.trim().length > 0);
  return match ? match.trim() : '';
};

const isDirectSubAccount = (value) => value === null || value === undefined || value === '';

const normalizeSubAccount = (value) => {
  if (isDirectSubAccount(value)) return null;
  return typeof value === 'string' ? value : String(value);
};

const isSubAccountEntity = (entity) => Boolean(entity && entity.accountType === 'sub');

const getEntityDisplayName = (entity) => {
  if (!entity) return '—';
  return entity.entityName || entity.name || entity.legalName || entity.displayName || '—';
};

  const load = async () => {
    setLoading(true);
    try {
      const [entsSnap, ledSnap, bankSnap] = await Promise.all([
        getDocs(query(collection(db, 'entities'), orderBy('entityName', 'asc'))),
        getDocs(query(collection(db, 'ledger'), orderBy('postedAt', 'desc'))),
        getDocs(collection(db, 'bank_lines')),
      ]);
      const ents = entsSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((e) => DIRECT_TYPES.includes(e.entityType));
      const rows = ledSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((r) => DIRECT_TYPES.includes(r.directAccountType));
      setEntities(ents);
      setLedger(rows);
      const bankSum = bankSnap.docs.reduce((sum, snap) => {
        const data = snap.data();
        return roundCurrency(sum + Number((data && data.amountUsd) || 0));
      }, 0);
      setBankTotal(roundCurrency(bankSum));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const entityMap = useMemo(() => new Map(entities.map((e) => [e.id, e])), [entities]);

  const resolveRowAttribution = (row) => {
    const directEntity = entityMap.get(row.directAccountId) || {};
    const normalizedSub = normalizeSubAccount(row.subAccount);
    if (normalizedSub) {
      return {
        isSubAccountRow: true,
        parentId: row.directAccountId,
        parentEntity: directEntity,
        subAccountId: normalizedSub,
        subAccountEntity: entityMap.get(normalizedSub) || {},
      };
    }
    if (isSubAccountEntity(directEntity) && directEntity.parentAccountId) {
      const parentEntity = entityMap.get(directEntity.parentAccountId) || {};
      return {
        isSubAccountRow: true,
        parentId: directEntity.parentAccountId,
        parentEntity,
        subAccountId: row.directAccountId,
        subAccountEntity: directEntity,
      };
    }
    return {
      isSubAccountRow: false,
      parentId: row.directAccountId,
      parentEntity: directEntity,
      subAccountId: null,
      subAccountEntity: null,
    };
  };

  // Resolve subaccount IDs to readable names by checking known labels and falling back to entity names
  useEffect(() => {
    const candidateIds = Array.from(new Set(
      ledger
        .map((row) => {
          const raw = row?.subAccount;
          if (raw === null || raw === undefined || raw === '') return null;
          return typeof raw === 'string' ? raw : String(raw);
        })
        .filter(Boolean)
    ));
    const missing = candidateIds
      .filter((id) => !partnerSubAccountLabels[id])
      .filter((id) => !subAccountNameById[id]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      try {
        for (let i = 0; i < missing.length; i += 10) {
          const chunk = missing.slice(i, i + 10);
          try {
            const snap = await getDocs(query(collection(db, 'entities'), where(documentId(), 'in', chunk)));
            snap.docs.forEach((docSnap) => {
              const data = docSnap.data() || {};
              updates[docSnap.id] = data.entityName || data.name || data.legalName || data.displayName || docSnap.id;
            });
          } catch (err) {
            chunk.forEach((id) => {
              if (!updates[id]) updates[id] = id;
            });
          }
        }
      } finally {
        if (!cancelled && Object.keys(updates).length > 0) {
          setSubAccountNameById((prev) => ({ ...prev, ...updates }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [ledger, subAccountNameById]);

  const inDate = (postedAt) => {
    const { start, end } = filters;
    if (!start && !end) return true;
    const d = new Date(postedAt);
    if (Number.isNaN(d.getTime())) return true;
    if (start && d < new Date(start)) return false;
    if (end) {
      const e = new Date(end);
      e.setHours(23, 59, 59, 999);
      if (d > e) return false;
    }
    return true;
  };

  const directBalances = useMemo(() => {
    const map = new Map();
    ledger.forEach((r) => {
      if (!inDate(r.postedAt)) return;
      if (filters.entityType && r.directAccountType !== filters.entityType) return;
      const attribution = resolveRowAttribution(r);
      // Always use the parent entity for aggregation (whether it's a direct or sub-account row)
      const entityId = attribution.parentId;
      const ent = attribution.parentEntity || {};
      if (isSubAccountEntity(ent)) return;
      const entityType = ent.entityType || r.directAccountType || '—';
      const accountTypeKey = entityType || r.directAccountType || '—';
      const status = ent.status || 'active';
      const entityName = getEntityDisplayName(ent);
      const key = `${entityId}__${accountTypeKey}`;
      const existing = map.get(key);
      if (existing) {
        existing.balance = roundCurrency(existing.balance + Number(r.amountUsd || 0));
      } else {
        map.set(key, {
          key,
          entityId,
          entityName,
          type: accountTypeKey,
          status,
          balance: roundCurrency(Number(r.amountUsd || 0)),
          directAccountType: accountTypeKey,
        });
      }
    });
    const filtered = Array.from(map.values())
      .filter((r) => (filters.includeInactive ? true : (r.status || 'active') !== 'inactive'))
      .filter((r) => (filters.includeZero ? true : Math.abs(r.balance) > 0.0001));
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    return filtered.sort((a, b) => {
      const nameCompare = collator.compare(a.entityName || '', b.entityName || '');
      if (nameCompare !== 0) return nameCompare;
      return collator.compare(a.type || '', b.type || '');
    });
  }, [ledger, entityMap, filters]);

  const displayRows = directBalances;

  const liabilitiesTotal = useMemo(() => {
    const total = ledger.reduce((sum, row) => {
      if (!inDate(row.postedAt)) return sum;
      if (filters.entityType && row.directAccountType !== filters.entityType) return sum;
      const ent = entityMap.get(row.directAccountId) || {};
      const status = ent.status || 'active';
      if (!filters.includeInactive && status === 'inactive') return sum;
      return roundCurrency(sum + Number(row.amountUsd || 0));
    }, 0);
    return roundCurrency(total);
  }, [ledger, entityMap, filters]);
  const netBalance = useMemo(() => roundCurrency(Number(bankTotal || 0) - liabilitiesTotal), [liabilitiesTotal, bankTotal]);

  const getTimeMs = (v) => {
    if (!v) return 0;
    if (typeof v === 'number') return v;
    if (v instanceof Date) return v.getTime();
    if (v && typeof v.toDate === 'function') return v.toDate().getTime();
    if (v && typeof v.toMillis === 'function') return v.toMillis();
    const parsed = Date.parse(v);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const drillRows = useMemo(() => {
    if (!drill.open) return [];
    // Combined history: direct entries plus every entry attributed to one of the entity's subaccounts
    return ledger
      .filter((r) => resolveRowAttribution(r).parentId === drill.entityId)
      .filter((r) => inDate(r.postedAt))
      .sort((a, b) => {
        const tB = getTimeMs(b.postedAt);
        const tA = getTimeMs(a.postedAt);
        if (tB !== tA) return tB - tA; // primary: postedAt desc
        const cB = getTimeMs(b.createdAt);
        const cA = getTimeMs(a.createdAt);
        if (cB !== cA) return cB - cA; // secondary: createdAt desc
        return String(b.id).localeCompare(String(a.id)); // final deterministic tiebreaker
      });
  }, [drill, ledger, filters, entityMap]);

  useEffect(() => {
    if (!drill.open) return;
    const ids = Array.from(new Set(drillRows.map((r) => r.contractId).filter(Boolean)));
    const missing = ids.filter((id) => !contractTitleById[id]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const pairs = await Promise.all(missing.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, 'contracts', id));
          const title = snap.exists() ? (snap.data().title || id) : id;
          return [id, title];
        } catch (err) {
          return [id, id];
        }
      }));
      if (cancelled) return;
      setContractTitleById((prev) => {
        const next = { ...prev };
        pairs.forEach(([id, title]) => { next[id] = title; });
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [drill.open, drillRows]);

  const getDrillRowSubAccountLabel = (row) => {
    const attribution = resolveRowAttribution(row);
    if (!attribution.isSubAccountRow) return 'Main Account';
    const subEntityName = getEntityDisplayName(attribution.subAccountEntity);
    if (subEntityName && subEntityName !== '—') return subEntityName;
    return resolveSubAccountLabel(row.directAccountType, attribution.subAccountId, subAccountNameById);
  };

  return (
    <Container maxWidth={false} sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <APIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">Accounts Payable</Typography>
        </Box>
        <Paper sx={{ p: 2, display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'space-between', minWidth: { xs: '100%', md: 420 } }}>
          {[
            { label: 'Liabilities', value: liabilitiesTotal },
            { label: 'Bank', value: bankTotal },
            { label: 'Balance', value: netBalance },
          ].map((item) => (
            <Box key={item.label} sx={{ minWidth: 110 }}>
              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
              <Typography variant="h6" fontWeight="bold" color={Number(item.value) >= 0 ? 'success.main' : 'error.main'}>
                {formatCurrency(item.value)}
              </Typography>
            </Box>
          ))}
        </Paper>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField select size="small" fullWidth label="Entity Type" value={filters.entityType} onChange={(e)=>setFilters({ ...filters, entityType: e.target.value })}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="company">Company</MenuItem>
              <MenuItem value="partner">Partner</MenuItem>
              <MenuItem value="vendor">Vendor</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '100%', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
              <FormControlLabel control={<Switch checked={filters.includeInactive} onChange={(e)=>setFilters({ ...filters, includeInactive: e.target.checked })} />} label="Inactives" />
              <FormControlLabel control={<Switch checked={filters.includeZero} onChange={(e)=>setFilters({ ...filters, includeZero: e.target.checked })} />} label="Balanced" />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <TableContainer sx={{ flex: 1, minHeight: 0 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Entity</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRows.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center">No results</TableCell></TableRow>
                ) : displayRows.map((r) => (
                  <TableRow key={r.key || r.entityId || r.entityName} hover>
                    <TableCell>
                      <Typography>{r.entityName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.type || '—'} variant="outlined" />
                    </TableCell>
                    <TableCell>{r.status || 'active'}</TableCell>
                    <TableCell align="right">
                      <Typography color={Number(r.balance) >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                        {formatCurrency(r.balance)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setDrill({
                          open: true,
                          entityId: r.entityId,
                          entityName: r.entityName,
                          type: r.type,
                        })}
                      >
                        View Ledger
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog
        open={drill.open}
        onClose={()=>setDrill({ open: false, entityId: '', entityName: '', type: '' })}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: { width: { xs: '100%', lg: '90vw' }, maxWidth: 1400 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="h6">Ledger — {drill.entityName}</Typography>
              <Typography variant="caption" color="text.secondary">
                Combined history: main account and all subaccounts
              </Typography>
            </Box>
            <Chip size="small" label={drill.type || '—'} />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Subaccount</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {drillRows.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center">No entries</TableCell></TableRow>
              ) : drillRows.map((e) => {
                const descriptionText = resolveDescription(e);
                const hasContractLink = (e.source === 'Contract') && e.contractId;
                const hasServiceMeta = Boolean(e.serviceName || e.serviceId);
                return (
                  <TableRow key={e.id}>
                    <TableCell>{e.postedAt}</TableCell>
                    <TableCell>{e.source}</TableCell>
                    <TableCell>{e.eventType}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{getDrillRowSubAccountLabel(e)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {descriptionText ? (
                          <Typography variant="body2">{descriptionText}</Typography>
                        ) : (!hasContractLink && !hasServiceMeta && (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        ))}
                        {hasContractLink && (
                          <Typography variant="caption" sx={{ display: 'block' }}>
                            <Link
                              component={RouterLink}
                              to={{ pathname: '/contracts', search: `?openContractId=${e.contractId}`, state: { openContractId: e.contractId } }}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                            >
                              {contractTitleById[e.contractId] || 'Contract'}
                            </Link>
                          </Typography>
                        )}
                        {hasServiceMeta && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {e.serviceName ? `Service: ${e.serviceName}` : `Service ID: ${e.serviceId}`}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography color={Number(e.amountUsd) >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">
                        {Number(e.amountUsd) >= 0 ? '+' : ''}{formatCurrency(e.amountUsd)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
              {drillRows.length > 0 && (
                <TableRow>
                  <TableCell colSpan={5}><Typography fontWeight="bold">Balance</Typography></TableCell>
                  <TableCell align="right"><Typography fontWeight="bold">{formatCurrency(roundCurrency(drillRows.reduce((s, r) => roundCurrency(s + Number(r.amountUsd || 0)), 0)))}</Typography></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDrill({ open: false, entityId: '', entityName: '', type: '' })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}


